import { Button } from 'primereact/button';
import { FC, Suspense, useEffect } from 'react';
import { ReportData, ReportGroupBy, ReportSubtype } from '../../../../models/Report';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectedCsvProcess } from '../../../../redux/reducers/csvProcessSlice';
import { PreloadedQuery } from 'react-relay';
import { ReportHubDiagnosticAssessmentTotalQuery } from '../../../../graphQLOperations/__generated__/ReportHubDiagnosticAssessmentTotalQuery.graphql';
import ReportHubSubFooter from './ReportHubSubFooter';
import { ReportHubSkillAssessmentsTotalQuery } from '../../../../graphQLOperations/__generated__/ReportHubSkillAssessmentsTotalQuery.graphql';
import { ReportHubOutstandingAssessmentsTotalQuery } from '../../../../graphQLOperations/__generated__/ReportHubOutstandingAssessmentsTotalQuery.graphql';
import { setReportHubFilter } from '../../../../redux/reducers/filterReportHubSlice';
import { setReportHubQuerysInProgress } from '../../../../redux/reducers/reportHubQuerysInProgress';
interface Props {
  values: ReportData;
  selectedReportSubtype: ReportSubtype | null;
  closeModalHandle: () => void;
  triggerDownload: () => void;
  triggerQueryKey: number | null;
  queryRef:
    | PreloadedQuery<ReportHubDiagnosticAssessmentTotalQuery, Record<string, unknown>>
    | PreloadedQuery<ReportHubSkillAssessmentsTotalQuery, Record<string, unknown>>
    | PreloadedQuery<ReportHubOutstandingAssessmentsTotalQuery, Record<string, unknown>>;
  isReduxFiltersUsed: boolean;
}

const getButtonID = (reportSubtype: ReportSubtype | null, groupedBy: ReportGroupBy | null) => {
  switch (reportSubtype) {
    case ReportSubtype.DIAGNOSTIC_SUMMARY:
      return 'dlDiagnosticSummary';
    case ReportSubtype.DIAGNOSTIC_STUDENTS_UNFINISHED_DIAGNOSTIC:
      return 'dlStudentsUnfinishedDiagnostics';
    case ReportSubtype.PROGRESS_DETAILED_SKILL_REPORT:
      return groupedBy === ReportGroupBy.UNIT ? 'dl_detailed_skill_unit' : 'dl_detailed_skill_standard';
    case ReportSubtype.PROGRESS_SKILL_ASSESSMENT_HISTORY:
      return 'dlSkillAssessmentHistory';
    default:
      return '';
  }
};

const ReportHubFooter: FC<Props> = ({
  values,
  selectedReportSubtype,
  triggerDownload,
  closeModalHandle,
  triggerQueryKey,
  queryRef,
  isReduxFiltersUsed,
}) => {
  const dispatch = useAppDispatch();
  const isCsvProcessing = useAppSelector(selectedCsvProcess);

  useEffect(() => {
    if (selectedReportSubtype === ReportSubtype.PROGRESS_DETAILED_SKILL_REPORT && triggerQueryKey) {
      dispatch(
        setReportHubFilter({
          selectedReportSubtype,
          totalRecords: 0,
          values,
          key: triggerQueryKey,
        }),
      );
      dispatch(setReportHubQuerysInProgress(true));
      closeModalHandle();
    }
  }, [triggerQueryKey]);

  const disabledButton = () => {
    return (
      isCsvProcessing ||
      values.classes.length === 0 ||
      (!isReduxFiltersUsed && values.sites.length === 0) ||
      (selectedReportSubtype !== ReportSubtype.DIAGNOSTIC_STUDENTS_UNFINISHED_DIAGNOSTIC && values.roadmap === '') ||
      (selectedReportSubtype === ReportSubtype.PROGRESS_DETAILED_SKILL_REPORT && values.standards === '')
    );
  };
  return (
    <>
      <Button onClick={closeModalHandle} label="Close" outlined className="border-none" />
      <Button
        id={getButtonID(selectedReportSubtype, values.groupedBy)}
        onClick={triggerDownload}
        disabled={disabledButton()}
        label="Download"
        icon="nc_icon-nc_export icon-sm"
        outlined
      />
      {queryRef && (
        <Suspense>
          <ReportHubSubFooter
            selectedReportSubtype={selectedReportSubtype}
            triggerQueryKey={triggerQueryKey}
            queryRef={queryRef}
            values={values}
            closeModalHandle={closeModalHandle}
          />
        </Suspense>
      )}
    </>
  );
};

export default ReportHubFooter;
